
import { initializeApp } from "firebase/app";
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  updateProfile, 
  sendEmailVerification, 
  sendPasswordResetEmail, 
  GoogleAuthProvider, 
  signInWithPopup, 
  signOut, 
  Auth, 
  User 
} from "firebase/auth";
import { 
  getFirestore, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  deleteDoc, 
  collection, 
  addDoc, 
  onSnapshot, 
  Firestore, 
  increment, 
  Timestamp, 
  query, 
  where 
} from "firebase/firestore";
import { getStorage, FirebaseStorage } from "firebase/storage";
import { getAnalytics } from "firebase/analytics";

const firebaseConfig = {
  apiKey: "AIzaSyBM7str2W1qQgqaSce2EHtpUl8QfxkUMs0",
  authDomain: "reshooter-6ed75.firebaseapp.com",
  projectId: "reshooter-6ed75",
  storageBucket: "reshooter-6ed75.firebasestorage.app",
  messagingSenderId: "891839851606",
  appId: "1:891839851606:web:8cce087622d2f5ba801ff7",
  measurementId: "G-GWQS716VJS"
};

// Initialize Firebase safely
let app;
let auth: Auth | undefined;
let db: Firestore | undefined;
let storage: FirebaseStorage | undefined;
let analytics;

try {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    db = getFirestore(app);
    storage = getStorage(app);
    if (typeof window !== 'undefined') {
        analytics = getAnalytics(app);
    }
} catch (error) {
    console.warn("Firebase initialization failed.", error);
}

// --- STRIPE CONFIGURATION ---
export const STRIPE_PRICES = {
    CREATOR: 'price_1SaQydQ7welwQcvE4arJtFIA', 
    PRO: 'price_1SaQzmQ7welwQcvEyfb5OdtT'
};

// --- FIRESTORE USER MANAGEMENT ---

export const createUserDocument = async (user: User, additionalData?: any) => {
    if (!db || !user) return;
    const userRef = doc(db, "users", user.uid);
    
    try {
        const snapshot = await getDoc(userRef);

        if (!snapshot.exists()) {
            const { displayName, email, photoURL } = user;
            const now = new Date();
            // Calculate renewal date (30 days from now)
            const nextMonth = new Date(now);
            nextMonth.setDate(nextMonth.getDate() + 30);

            await setDoc(userRef, {
                uid: user.uid,
                name: displayName || additionalData?.name || '',
                email,
                photoFileName: photoURL || '',
                credits: 5, // Sign-up Bonus (TRIAL Level / RESHOOTS)
                plan: 'trial',
                isAdmin: false, 
                hasCompletedOnboarding: false, // New field for tutorial tracking
                createdAt: now,
                renewsAt: nextMonth, // Set renewal date
                ...additionalData
            });
        }
        return getUserDocument(user.uid);
    } catch (error) {
        console.error("Error creating user document", error);
    }
};

export const completeOnboarding = async (uid: string) => {
    if (!db || !uid) return;
    try {
        await updateDoc(doc(db, "users", uid), {
            hasCompletedOnboarding: true
        });
    } catch (e) {
        console.error("Failed to mark onboarding complete", e);
    }
};

export const getUserDocument = async (uid: string) => {
    if (!db || !uid) return null;
    try {
        const userRef = doc(db, "users", uid);
        const snapshot = await getDoc(userRef);
        if (snapshot.exists()) return snapshot.data();
    } catch (error) {
        console.error("Error fetching user", error);
    }
    return null;
};

// Real-time listener for user data (RESHOOTS, etc)
export const subscribeToUserDocument = (uid: string, callback: (data: any) => void) => {
    if (!db || !uid) return () => {};
    const userRef = doc(db, "users", uid);
    return onSnapshot(userRef, (doc) => {
        if (doc.exists()) {
            callback(doc.data());
        }
    });
};

export const updateUserDocument = async (uid: string, data: any) => {
    if (!db || !uid) return;
    try {
        const userRef = doc(db, "users", uid);
        await updateDoc(userRef, data);
        return true;
    } catch (error) {
        console.error("Error updating user", error);
        throw error;
    }
};

export const deleteUserDocument = async (uid: string) => {
    if (!db || !uid) return;
    try {
        await deleteDoc(doc(db, "users", uid));
    } catch (error) {
        console.error("Error deleting user doc", error);
        throw error;
    }
};

// --- SUPPORT TICKETS ---

export const createSupportTicket = async (userId: string, email: string, subject: string, message: string) => {
    if (!db) return;
    try {
        await addDoc(collection(db, "support_tickets"), {
            userId,
            email,
            subject,
            message,
            status: 'open',
            createdAt: new Date()
        });
        return true;
    } catch (error) {
        console.error("Error creating support ticket", error);
        throw error;
    }
};

// --- RESHOOT SYSTEM LOGIC ---

export const manageCredits = async (uid: string) => {
    if (!db || !uid) return;
    const userRef = doc(db, "users", uid);
    
    try {
        const snapshot = await getDoc(userRef);
        if (!snapshot.exists()) return;

        const data = snapshot.data();
        const now = Date.now();
        
        // Handle renewal date logic
        let renewsAt = 0;
        if (data.renewsAt) {
            if (typeof data.renewsAt.toMillis === 'function') {
                renewsAt = data.renewsAt.toMillis();
            } else if (data.renewsAt instanceof Date) {
                renewsAt = data.renewsAt.getTime();
            } else if (typeof data.renewsAt === 'string' || typeof data.renewsAt === 'number') {
                renewsAt = new Date(data.renewsAt).getTime();
            }
        }
        
        if (!renewsAt || isNaN(renewsAt)) {
            const createdAt = data.createdAt ? (data.createdAt.toMillis ? data.createdAt.toMillis() : new Date(data.createdAt).getTime()) : now;
            renewsAt = createdAt + (30 * 24 * 60 * 60 * 1000);
            await updateDoc(userRef, { renewsAt: new Date(renewsAt) });
        }

        // Check renewal
        if (now > renewsAt) {
            console.log(`[RESHOOTS] Renewal date passed (${new Date(renewsAt).toLocaleDateString()}). Renewing RESHOOTS for user ${uid}`);
            
            // Determine allowance based on plan
            let monthlyAllowance = 5; // Trial/Free
            if (data.plan === 'creator' || data.plan === 'silver' || data.plan === 'basic') monthlyAllowance = 50;
            if (data.plan === 'pro' || data.plan === 'gold') monthlyAllowance = 125;

            // Calculate NEXT renewal date
            let nextRenewal = renewsAt;
            while (now > nextRenewal) {
                nextRenewal += (30 * 24 * 60 * 60 * 1000);
            }

            await updateDoc(userRef, {
                credits: monthlyAllowance,
                renewsAt: new Date(nextRenewal)
            });
        }
    } catch (e) {
        console.error("[RESHOOTS] Failed to manage RESHOOTS:", e);
    }
};

export const deductCredit = async (uid: string, cost: number = 1): Promise<boolean> => {
    if (!db || !uid) return false;
    const userRef = doc(db, "users", uid);

    try {
        const snapshot = await getDoc(userRef);
        if (!snapshot.exists()) return false;
        
        const data = snapshot.data();

        // 1. ADMIN BYPASS
        if (data.isAdmin === true) {
            console.log("[RESHOOTS] Admin bypass active.");
            return true;
        }

        // 2. Check Balance
        if (data.credits >= cost) {
            await updateDoc(userRef, {
                credits: increment(-cost)
            });
            return true;
        } else {
            console.warn(`[RESHOOTS] Insufficient RESHOOTS. Need ${cost}, has ${data.credits}.`);
            return false;
        }
    } catch (e) {
        console.error("[RESHOOTS] Deduction failed:", e);
        return false;
    }
};

export const redeemPromoCode = async (uid: string, code: string) => {
    if (!db || !uid) throw new Error("Database unavailable");
    
    const codeId = code.toUpperCase().trim();
    // 1. Reference specific promo code doc
    const promoRef = doc(db, "promo_codes", codeId);
    // 2. Reference user's redemption record
    const redemptionRef = doc(db, "users", uid, "redemptions", codeId);
    const userRef = doc(db, "users", uid);

    try {
        let promoValue = 0;
        let promoExists = false;

        // Try checking DB
        try {
            const promoSnap = await getDoc(promoRef);
            if (promoSnap.exists()) {
                const data = promoSnap.data();
                if (data.active === false) throw new Error("This code has expired.");
                promoValue = data.value || 20;
                promoExists = true;
            }
        } catch (dbError) {
            console.warn("DB Promo Check Failed (likely permissions)", dbError);
        }

        // Fallback for default code if DB check failed or code not found in DB
        if (!promoExists) {
            if (codeId === 'RESHOOT20') { 
                promoValue = 20;
                promoExists = true;
            } else {
                throw new Error("Invalid promo code.");
            }
        }

        // Check if already redeemed
        const redemptionSnap = await getDoc(redemptionRef);
        if (redemptionSnap.exists()) {
            throw new Error("You have already used this code.");
        }

        // Apply Credit & Record Redemption
        await setDoc(redemptionRef, { redeemedAt: new Date(), code: codeId, value: promoValue });
        await updateDoc(userRef, { credits: increment(promoValue) });

        return promoValue;
    } catch (e) {
        console.error("Promo code failed", e);
        throw e;
    }
};

// --- ADMIN UTILS (For adding promo codes) ---

export const createPromoCode = async (code: string, value: number) => {
    if (!db) return;
    const codeId = code.toUpperCase().trim();
    try {
        await setDoc(doc(db, "promo_codes", codeId), {
            value: value,
            active: true,
            createdAt: new Date()
        });
        return true;
    } catch (e) {
        console.error("Create Promo Code Failed", e);
        throw e;
    }
};

export const toggleAdminStatus = async (uid: string, isAdmin: boolean) => {
    if (!db || !uid) return;
    await updateDoc(doc(db, "users", uid), { isAdmin });
};

// --- AUTH FUNCTIONS ---

export const loginUser = async (email: string, password: string) => {
    if (!auth) {
        throw new Error("Authentication service unavailable.");
    }
    return signInWithEmailAndPassword(auth, email, password);
};

export const registerUser = async (email: string, password: string) => {
    if (!auth) {
        throw new Error("Authentication service unavailable.");
    }
    return createUserWithEmailAndPassword(auth, email, password);
};

export const updateUserProfile = async (user: any, name: string) => {
    if (!user) return;
    return updateProfile(user, { displayName: name });
};

export const logoutUser = async () => {
    if (!auth) return;
    return signOut(auth);
};

export const verifyUserEmail = async (user: User) => {
    return sendEmailVerification(user);
};

export const passwordReset = async (email: string) => {
    if (!auth) return;
    return sendPasswordResetEmail(auth, email);
};

export const signInWithGoogle = async () => {
    if (!auth) throw new Error("Auth unavailable");
    const provider = new GoogleAuthProvider();
    return signInWithPopup(auth, provider);
};

// --- STRIPE PAYMENTS & SUBSCRIPTIONS ---

export const createStripeCheckoutSession = async (priceId: string) => {
    if (!auth || !db) throw new Error("Firebase not initialized.");
    
    const currentUser = auth.currentUser;
    if (!currentUser) {
        console.error("[Stripe] No authenticated user found.");
        throw new Error("You must be logged in to subscribe.");
    }

    const userId = currentUser.uid;
    console.log(`[Stripe] Creating checkout session for User: ${userId}, Price: ${priceId}`);

    try {
        const sessionsRef = collection(db, 'customers', userId, 'checkout_sessions');
        
        const docRef = await addDoc(sessionsRef, {
            price: priceId,
            success_url: "https://reshooter.com",
            cancel_url: "https://reshooter.com",
            client_reference_id: userId
        });

        return new Promise<string>((resolve, reject) => {
            const unsubscribe = onSnapshot(docRef, (snap) => {
                const data = snap.data();
                if (!data) return; 
                
                const { error, url } = data;
                
                if (error) {
                    unsubscribe();
                    reject(new Error(`Payment Error: ${error.message}`));
                }
                
                if (url) {
                    unsubscribe();
                    resolve(url);
                }
            }, (err) => {
                if (err.code === 'permission-denied') {
                    reject(new Error("Permission Denied: Unable to read checkout session."));
                } else {
                    reject(new Error(`Connection Error: ${err.message}`));
                }
            });
        });

    } catch (e: any) {
        console.error("[Stripe] Failed to create document:", e);
        throw e;
    }
};

export const createCustomerPortalSession = async () => {
    if (!auth || !db) throw new Error("Firebase not initialized.");
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("Not logged in.");

    const userId = currentUser.uid;
    try {
        const portalRef = collection(db, 'customers', userId, 'portal_sessions');
        const docRef = await addDoc(portalRef, {
            returnUrl: window.location.origin
        });

        return new Promise<string>((resolve, reject) => {
             const unsubscribe = onSnapshot(docRef, (snap) => {
                const data = snap.data();
                if (!data) return;
                if (data.url) {
                    unsubscribe();
                    resolve(data.url);
                }
                if (data.error) {
                    unsubscribe();
                    reject(new Error(data.error.message));
                }
             });
        });

    } catch (e: any) {
        console.error("Portal Error", e);
        throw new Error("Could not redirect to subscription management.");
    }
};

export const setupSubscriptionListener = (uid: string) => {
    if (!db || !uid) return () => {};

    const q = query(
        collection(db, 'customers', uid, 'subscriptions'), 
        where('status', 'in', ['active', 'trialing'])
    );

    return onSnapshot(q, async (snapshot) => {
        // Default to TRIAL (Free)
        let newPlan = 'trial'; 
        let stripePeriodEnd = 0;

        if (snapshot.empty) {
            console.log('[Stripe Sync] No active subscription found. Plan = TRIAL.');
        } else {
            const subscription = snapshot.docs[0].data();
            const items = subscription.items || [];
            
            if (subscription.current_period_end && subscription.current_period_end.seconds) {
                stripePeriodEnd = subscription.current_period_end.seconds * 1000;
            }
            
            for (const item of items) {
                const priceId = item.price.id;
                if (priceId === STRIPE_PRICES.PRO) {
                    newPlan = 'pro';
                    break;
                } else if (priceId === STRIPE_PRICES.CREATOR) {
                    newPlan = 'creator';
                    break;
                }
            }
            console.log(`[Stripe Sync] Active subscription found. Plan = ${newPlan}`);
        }

        const userRef = doc(db!, "users", uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
            const userData = userSnap.data();
            const currentPlan = userData.plan || 'trial';
            const currentRenewsAt = userData.renewsAt ? (userData.renewsAt.toMillis ? userData.renewsAt.toMillis() : new Date(userData.renewsAt).getTime()) : 0;

            const updates: any = {};
            let shouldUpdate = false;

            // Handle Plan Changes
            if (currentPlan !== newPlan) {
                updates.plan = newPlan;
                shouldUpdate = true;

                if (newPlan !== 'trial') {
                    let newAllowance = 50; // Creator default
                    if (newPlan === 'pro') newAllowance = 125;

                    const nextMonth = new Date();
                    nextMonth.setDate(nextMonth.getDate() + 30);

                    updates.credits = newAllowance;
                    updates.renewsAt = stripePeriodEnd ? new Date(stripePeriodEnd) : nextMonth;
                }
            }
            // Handle Renewals
            else if (newPlan !== 'trial' && stripePeriodEnd > (currentRenewsAt + 86400000)) {
                let newAllowance = 50;
                if (newPlan === 'pro') newAllowance = 125;
                
                updates.credits = newAllowance;
                updates.renewsAt = new Date(stripePeriodEnd);
                shouldUpdate = true;
            }

            if (shouldUpdate) {
                await updateDoc(userRef, updates);
                console.log(`[Stripe Sync] Updated user document:`, updates);
            }
        }
    });
};

export { auth, db, storage };
